import React from "react";
import { WithdrawalRequest } from "interfaces/commerce/withdrawal-request";
import styles from "./withdrawal-request-item.module.css";
import { PaynoteFundingSource } from "interfaces/commerce/paynote/paynote-funding-source.ts";

interface WithdrawalRequestItemProps {
  withdrawalRequest: WithdrawalRequest;
  fundingSource?: PaynoteFundingSource;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "PENDING_REVIEW":
      return "#FFA500"; // Orange
    case "APPROVED":
      return "#32CD32"; // Lime green
    case "REJECTED":
      return "#FF4444"; // Red
    case "PROCESSING":
      return "#4A90E2"; // Blue
    case "COMPLETED":
      return "#00AA00"; // Green
    case "FAILED":
    case "ERROR":
      return "#FF0000"; // Red
    case "CANCELLED_BY_USER":
      return "#888888"; // Gray
    default:
      return "#666666"; // Default gray
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "PENDING_REVIEW":
      return "Pending Review";
    case "APPROVED":
      return "Approved";
    case "REJECTED":
      return "Rejected";
    case "PROCESSING":
      return "Processing";
    case "COMPLETED":
      return "Completed";
    case "FAILED":
      return "Failed";
    case "ERROR":
      return "Error";
    case "CANCELLED_BY_USER":
      return "Cancelled";
    default:
      return status;
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const WithdrawalRequestItem: React.FC<WithdrawalRequestItemProps> = ({
  withdrawalRequest,
  fundingSource,
}) => {
  const statusColor = getStatusColor(withdrawalRequest.status);
  const statusLabel = getStatusLabel(withdrawalRequest.status);

  // Extract bank info from audit log as fallback
  const getBankInfo = () => {
    if (fundingSource && fundingSource.last4) {
      return {
        bankName: fundingSource.bankName,
        accountEnding: fundingSource.last4
      };
    }
    
    // Fallback: extract from audit log
    const paynoteApiEntry = withdrawalRequest.audit_log?.find(
      entry => entry.event_type === "PAYNOTE_API_SUCCESS"
    );
    
    if (paynoteApiEntry?.params?.paynote_transaction) {
      const transaction = paynoteApiEntry.params.paynote_transaction;
      return {
        bankName: transaction.rec_bname || "Bank",
        accountEnding: transaction.rec_lbacc || "****"
      };
    }
    
    return null;
  };

  const bankInfo = getBankInfo();

  return (
    <div className={styles.withdrawalRequestItem}>
      <div className={styles.header}>
        <div className={styles.amount}>
          ${withdrawalRequest.amount.toFixed(2)}
        </div>
        <div 
          className={styles.status} 
          style={{ color: statusColor }}
        >
          {statusLabel}
        </div>
      </div>
      
      <div className={styles.details}>
        <div className={styles.detailRow}>
          <span className={styles.label}>Method:</span>
          <span className={styles.value}>{withdrawalRequest.withdrawal_method}</span>
        </div>
        
        {bankInfo && (
          <div className={styles.detailRow}>
            <span className={styles.label}>Bank Account:</span>
            <span className={styles.value}>
              {bankInfo.bankName} ••••{bankInfo.accountEnding}
            </span>
          </div>
        )}
        
        <div className={styles.detailRow}>
          <span className={styles.label}>Requested:</span>
          <span className={styles.value}>
            {formatDate(withdrawalRequest.created_at)}
          </span>
        </div>
        
        {withdrawalRequest.processed_at && (
          <div className={styles.detailRow}>
            <span className={styles.label}>Processed:</span>
            <span className={styles.value}>
              {formatDate(withdrawalRequest.processed_at)}
            </span>
          </div>
        )}
        
        {withdrawalRequest.admin_notes && (
          <div className={`${styles.detailRow} ${styles.notesRow}`}>
            <span className={styles.label}>Notes:</span>
            <span className={styles.value}>
              {withdrawalRequest.admin_notes}
            </span>
          </div>
        )}
        
        {withdrawalRequest.error && (
          <div className={`${styles.detailRow} ${styles.errorRow}`}>
            <span className={styles.label}>Issue:</span>
            <span className={styles.value} style={{ color: "#FF4444" }}>
              There was an issue processing your withdrawal. Please contact our support team at support@betsparket.com for assistance.
            </span>
          </div>
        )}
      </div>
      
      <div className={styles.footer}>
        <span className={styles.requestId}>
          ID: {withdrawalRequest.id}
        </span>
      </div>
    </div>
  );
};

export default WithdrawalRequestItem; 